import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, IconButton } from '@mui/material';
import { Close, SearchOutlined } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject } from '@syncfusion/ej2-react-grids';
import { useGetAssetsQuery } from '../../../../services/AssetService/assetApi';
import { AssetListView } from '../../../../services/AssetService/types/assetTypes';

interface AssetPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: AssetListView) => void;
  centerId: string;
}

export const AssetPickerDialog: React.FC<AssetPickerDialogProps> = ({ open, onClose, onSelect, centerId }) => {
  const [searchText, setSearchText] = useState('');
  const { data: assets, isLoading } = useGetAssetsQuery({ status: 'ACTV', center: centerId }, { skip: !centerId || !open });

  const filteredAssets = useMemo(() => {
    if (!assets) return [];
    if (!searchText.trim()) return assets;
    const search = searchText.toLowerCase();
    return assets.filter(asset =>
      asset.name.toLowerCase().includes(search) ||
      asset.assetTag.toLowerCase().includes(search) ||
      asset.categoryName.toLowerCase().includes(search) ||
      asset.building.toLowerCase().includes(search)
    );
  }, [assets, searchText]);

  const handleRowSelected = (args: any) => {
    if (args?.data) {
      onSelect(args.data as AssetListView);
      setSearchText('');
      onClose();
    }
  };

  const handleClose = () => {
    setSearchText('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Select Asset
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="Search by name, tag, category, or building..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
          slotProps={{
            input: {
              startAdornment: <SearchOutlined sx={{ color: '#64748b', fontSize: 18, mr: 1 }} />
            }
          }}
        />

        {isLoading ? (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
            Loading assets...
          </Typography>
        ) : filteredAssets.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
            {searchText ? 'No assets match your search.' : 'No active assets found for this center.'}
          </Typography>
        ) : (
          <Box>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#64748b', fontWeight: 500 }}>
              Showing {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} — click a row to select
            </Typography>
            <GridComponent
              dataSource={filteredAssets}
              allowPaging={true}
              allowSorting={true}
              pageSettings={{ pageSize: 10 }}
              sortSettings={{ columns: [{ field: 'name', direction: 'Ascending' as any }] }}
              height={350}
              rowHeight={36}
              gridLines="Both"
              rowSelected={handleRowSelected}
              cssClass="clickable-rows"
            >
              <ColumnsDirective>
                <ColumnDirective field="name" headerText="Name" width="160" textAlign="Left" />
                <ColumnDirective field="assetTag" headerText="Tag" width="100" textAlign="Left" />
                <ColumnDirective field="categoryName" headerText="Category" width="120" textAlign="Left" />
                <ColumnDirective field="building" headerText="Building" width="100" textAlign="Left" />
              </ColumnsDirective>
              <Inject services={[Page, Sort]} />
            </GridComponent>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetPickerDialog;
